import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getJiraDashboardManager,
  getJiraDashboardManagerCharge,
  getJiraDashboardManagerCompletude,
  getDashboardManagerAlertes,
} from "../services/api";
import GraphiqueBarres from "../components/GraphiqueBarres";
import GraphiqueNuage from "../components/GraphiqueNuage";

// ===================================================================
// The Crowd v2 — Pilotage › Dashboard
//
// Adaptation v2 de pages/DashboardManager.js : memes cinq appels API,
// memes indicateurs, memes seuils. Plein ecran, emojis retires,
// graphiques sur les composants partages et couleurs du theme.
//
// Seule correction : les tris (« hors tickets », barres) se font sur une
// copie. L'original triait en place les donnees de l'etat pendant le
// rendu, ce qui reordonnait aussi le tableau de charge.
//
// Refonte du module Projets : le bloc « Suivi projets » est retire. Il
// comptait les taches « Projet », desactivees par la reprise. Le suivi des
// projets est dans Pilotage › Projets (lien dans l'en-tete). Les heures
// « Projet » de la charge comptent desormais aussi les etapes (jira.py).
// ===================================================================

const PERIODES = [
  { value: "semaine", label: "Semaine" },
  { value: "mois", label: "Mois" },
];

const FILE_LABELS = {
  HEL: "Helpdesk",
  GDC: "Gestion des Comptes",
  DEVMGT: "DeviceMgmt",
  CHG: "Change Management",
};

// Segments de la barre empilee : trois nuances de la teinte du texte
const SEGMENTS = [
  { cle: "heures_recurrent", libelle: "Récurrent", classe: "v2-empile-1" },
  { cle: "heures_specifique", libelle: "Spécifique", classe: "v2-empile-2" },
  { cle: "heures_projet", libelle: "Projet", classe: "v2-empile-3" },
];

// Seuils repris de l'original (en heures, quelle que soit la periode)
function tonHeures(h) {
  if (h >= 30) return "success";
  if (h >= 15) return "warning";
  return "danger";
}

function BarreEmpilee({ ligne }) {
  const total = ligne.total_heures;
  if (!total) return <div className="v2-empile v2-empile-vide" />;
  return (
    <div className="v2-empile">
      {SEGMENTS.map((s) => {
        const pct = (ligne[s.cle] / total) * 100;
        if (pct <= 0) return null;
        return (
          <div
            key={s.cle}
            className={`v2-empile-segment ${s.classe}`}
            style={{ width: `${pct}%` }}
            title={`${s.libelle} : ${ligne[s.cle].toFixed(1)}h (${pct.toFixed(0)} %)`}
          >
            {pct > 15 && `${pct.toFixed(0)} %`}
          </div>
        );
      })}
    </div>
  );
}

function DashboardManagerV2({ collaborateur }) {
  const navigate = useNavigate();
  const [periode, setPeriode] = useState("semaine");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataJira, setDataJira] = useState(null);
  const [dataCharge, setDataCharge] = useState(null);
  const [dataCompletude, setDataCompletude] = useState(null);
  const [dataAlertes, setDataAlertes] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!collaborateur || !collaborateur.id) {
      setError("Collaborateur non identifié");
      setLoading(false);
      return;
    }
    try {
      const [jira, charge, completude, alertes] = await Promise.all([
        getJiraDashboardManager(collaborateur.id, periode),
        getJiraDashboardManagerCharge(collaborateur.id, periode),
        getJiraDashboardManagerCompletude(collaborateur.id),
        getDashboardManagerAlertes(collaborateur.id),
      ]);
      setDataJira(jira);
      setDataCharge(charge);
      setDataCompletude(completude);
      setDataAlertes(alertes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [collaborateur, periode]);

  useEffect(() => { charger(); }, [charger]);

  const entete = (
    <div className="v2-bib-entete">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Dashboard</h1>
        <p className="v2-sous-titre">
          Activité de l'équipe : saisie des temps et tickets Jira. Le suivi des projets est dans{" "}
          <button type="button" className="v2-lien-ligne" onClick={() => navigate("/v2/pilotage/projets")}>Pilotage › Projets</button>.
        </p>
      </header>
      <div className="v2-bascule" role="group" aria-label="Période">
        {PERIODES.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`v2-bascule-choix${periode === p.value ? " actif" : ""}`}
            aria-pressed={periode === p.value}
            onClick={() => setPeriode(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <div className="v2-page v2-page-module">{entete}<div className="v2-chargement">Chargement du dashboard…</div></div>;
  }
  if (error) {
    return <div className="v2-page v2-page-module">{entete}<div className="v2-msg v2-msg-erreur">Erreur : {error}</div></div>;
  }

  const vue = dataCharge?.vue_croisee || [];
  const parCollab = dataJira?.tickets_par_collaborateur || [];
  const alertesActives =
    dataAlertes &&
    (dataAlertes.collaborateurs_sans_saisie.count > 0 ||
      dataAlertes.tickets_anciens.count > 0 ||
      dataAlertes.projets_alerte_date.count > 0 ||
      dataAlertes.projets_stagnation.count > 0);

  const barresResolus = [...parCollab]
    .filter((c) => c.tickets_resolus > 0)
    .sort((a, b) => b.tickets_resolus - a.tickets_resolus)
    .map((c) => ({ label: `${c.prenom} ${c.nom}`, value: c.tickets_resolus }));
  const barresEnCours = [...parCollab]
    .filter((c) => c.tickets_en_cours > 0)
    .sort((a, b) => b.tickets_en_cours - a.tickets_en_cours)
    .map((c) => ({ label: `${c.prenom} ${c.nom}`, value: c.tickets_en_cours }));
  const horsTickets = [...vue].sort((a, b) => b.pct_hors_tickets - a.pct_hors_tickets);
  const tauxCompletude = dataCompletude?.taux_completude ?? 0;

  return (
    <div className="v2-page v2-page-module">
      {entete}

      {/* ── Points d'attention ── */}
      {alertesActives && (
        <section className="v2-dash-alertes" aria-label="Points d'attention">
          <h2 className="v2-bloc-titre">Points d'attention</h2>
          <ul className="v2-dash-alertes-liste">
            {dataAlertes.collaborateurs_sans_saisie.count > 0 && (
              <li>
                <strong>{dataAlertes.collaborateurs_sans_saisie.count} collaborateur(s)</strong> sans saisie cette semaine :{" "}
                {dataAlertes.collaborateurs_sans_saisie.detail.map((c) => `${c.prenom} ${c.nom}`).join(", ")}
              </li>
            )}
            {dataAlertes.tickets_anciens.count > 0 && (
              <li><strong>{dataAlertes.tickets_anciens.count} ticket(s)</strong> en cours depuis plus de 14 jours</li>
            )}
            {dataAlertes.projets_alerte_date.count > 0 && (
              <li><strong>{dataAlertes.projets_alerte_date.count} projet(s)</strong> avec une date de fin proche ou dépassée</li>
            )}
            {dataAlertes.projets_stagnation.count > 0 && (
              <li><strong>{dataAlertes.projets_stagnation.count} projet(s)</strong> sans mise à jour depuis 14 jours ou plus</li>
            )}
          </ul>
        </section>
      )}

      {/* ── Vue d'ensemble ── */}
      {dataJira && dataCompletude && (
        <div className="v2-dash-stats">
          <div className="v2-stat">
            <span className="v2-stat-libelle">Heures saisies</span>
            <span className="v2-stat-valeur">{vue.reduce((s, c) => s + c.total_heures, 0).toFixed(0)} h</span>
            <span className="v2-stat-aide">Période : {periode}</span>
          </div>
          <div className="v2-stat">
            <span className="v2-stat-libelle">Tickets résolus</span>
            <span className="v2-stat-valeur">{dataJira.kpi_globaux.total_resolus_equipe}</span>
            <span className="v2-stat-aide">{(dataJira.kpi_globaux.taux_resolution ?? 0).toFixed(1)} % de résolution</span>
          </div>
          <div className="v2-stat">
            <span className="v2-stat-libelle">Tickets en cours</span>
            <span className="v2-stat-valeur">{dataJira.kpi_globaux.total_en_cours_equipe}</span>
            <span className="v2-stat-aide">Équipe technique</span>
          </div>
          <div className="v2-stat">
            <span className="v2-stat-libelle">Complétude saisie</span>
            <span className={`v2-stat-valeur v2-texte-${tauxCompletude >= 80 ? "success" : "danger"}`}>
              {dataCompletude.nb_ont_saisi}/{dataCompletude.nb_total_collaborateurs}
            </span>
            <span className="v2-stat-aide">{tauxCompletude.toFixed(0)} % de l'équipe</span>
          </div>
        </div>
      )}

      {/* ── Charge par collaborateur ── */}
      {dataCharge && dataJira && (
        <section className="v2-dash-section" aria-labelledby="v2-dash-charge">
          <div className="v2-section-titre">
            <h2 id="v2-dash-charge">Charge par collaborateur</h2>
            <span className="v2-section-filet" />
            <span className="v2-empile-legende">
              {SEGMENTS.map((s) => (
                <span key={s.cle} className="v2-empile-legende-item">
                  <span className={`v2-empile-pastille ${s.classe}`} aria-hidden="true" />
                  {s.libelle}
                </span>
              ))}
            </span>
          </div>
          <div className="v2-panneau v2-tableau-conteneur">
            <table className="v2-tableau v2-dash-charge">
              <thead>
                <tr>
                  <th scope="col">Collaborateur</th>
                  <th scope="col" className="v2-num">Heures</th>
                  <th scope="col" className="v2-dash-col-repartition">Répartition</th>
                  <th scope="col" className="v2-num">Tickets résolus</th>
                  <th scope="col" className="v2-num">Tickets en cours</th>
                  <th scope="col" className="v2-num">Saisie</th>
                </tr>
              </thead>
              <tbody>
                {vue.length === 0 && <tr><td colSpan={6} className="v2-vide">Aucun collaborateur.</td></tr>}
                {vue.map((c) => {
                  const jira = parCollab.find((j) => j.collaborateur_id === c.collaborateur_id);
                  const comp = dataCompletude?.detail_collaborateurs.find((x) => x.collaborateur_id === c.collaborateur_id);
                  return (
                    <tr key={c.collaborateur_id}>
                      <td>{c.prenom} {c.nom}</td>
                      <td className={`v2-num v2-gras v2-texte-${tonHeures(c.total_heures)}`}>{c.total_heures.toFixed(1)} h</td>
                      <td><BarreEmpilee ligne={c} /></td>
                      <td className="v2-num v2-gras">{jira ? jira.tickets_resolus : 0}</td>
                      <td className="v2-num v2-gras">{jira ? jira.tickets_en_cours : 0}</td>
                      <td className="v2-num">
                        {comp && (
                          <span className={`v2-statut ${comp.a_saisi ? "v2-statut-success" : "v2-statut-danger"}`}>
                            {comp.a_saisi ? "Saisi" : "Non saisi"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Activite Jira ── */}
      {dataJira && (
        <section className="v2-dash-section" aria-labelledby="v2-dash-jira">
          <div className="v2-section-titre">
            <h2 id="v2-dash-jira">Activité Jira de l'équipe</h2>
            <span className="v2-section-filet" />
          </div>

          <div className="v2-dash-grille">
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Tickets résolus par collaborateur</h3>
              <GraphiqueBarres data={barresResolus} labelKey="label" valueKey="value" />
            </div>
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Tickets en cours par collaborateur</h3>
              <GraphiqueBarres data={barresEnCours} labelKey="label" valueKey="value" />
            </div>
          </div>

          {dataJira.repartition_par_file.length > 0 && (
            <div className="v2-dash-files">
              {dataJira.repartition_par_file.map((f) => (
                <div key={f.file_jira} className="v2-stat">
                  <span className="v2-stat-libelle">{FILE_LABELS[f.file_jira] || f.file_jira}</span>
                  <span className="v2-stat-valeur">{f.total_tickets}</span>
                  <span className="v2-stat-aide">{f.resolus} résolus · {f.en_cours} en cours</span>
                </div>
              ))}
            </div>
          )}

          {dataJira.temps_moyen_par_collaborateur && dataJira.temps_moyen_par_collaborateur.length > 0 && (
            <div className="v2-panneau v2-dash-temps">
              <h3 className="v2-panneau-titre">Temps moyen de résolution par collaborateur</h3>
              <div className="v2-tableau-conteneur">
                <table className="v2-tableau">
                  <thead>
                    <tr>
                      <th scope="col">Collaborateur</th>
                      <th scope="col">File</th>
                      <th scope="col" className="v2-num">Temps moyen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataJira.temps_moyen_par_collaborateur.map((r, i) => (
                      <tr key={i}>
                        <td>{r.prenom} {r.nom}</td>
                        <td><span className="v2-statut v2-statut-neutre">{FILE_LABELS[r.file_jira] || r.file_jira}</span></td>
                        <td className="v2-num v2-gras">{r.heures_moyenne ? `${r.heures_moyenne.toFixed(1)} h` : "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Vue croisee ── */}
      {dataCharge && dataCharge.vue_croisee && (
        <section className="v2-dash-section" aria-labelledby="v2-dash-croisee">
          <div className="v2-section-titre">
            <h2 id="v2-dash-croisee">Vue croisée : heures et tickets</h2>
            <span className="v2-section-filet" />
          </div>
          <div className="v2-dash-grille">
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Corrélation heures saisies / tickets résolus</h3>
              <GraphiqueNuage
                data={vue}
                xKey="total_heures"
                yKey="tickets_resolus"
                libelle={(d) => `${d.prenom} ${d.nom}`}
                titreX="Heures saisies"
                titreY="Tickets résolus"
                formatX={(v) => `${v.toFixed(1)}h`}
                formatY={(v) => `${v} tickets`}
              />
              <p className="v2-champ-aide">
                Distingue les collaborateurs en charge projets (beaucoup d'heures, peu de tickets) de ceux en support
                (peu d'heures, beaucoup de tickets).
              </p>
            </div>
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Répartition du temps « hors tickets »</h3>
              <div className="v2-tableau-conteneur v2-dash-hors-tickets">
                <table className="v2-tableau">
                  <thead>
                    <tr>
                      <th scope="col">Collaborateur</th>
                      <th scope="col" className="v2-num">% hors tickets</th>
                      <th scope="col">Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {horsTickets.map((c) => (
                      <tr key={c.collaborateur_id}>
                        <td>{c.prenom} {c.nom}</td>
                        <td className="v2-num v2-gras">{c.pct_hors_tickets.toFixed(0)} %</td>
                        <td className="v2-cellule-discrete">
                          Récurrent {c.heures_recurrent.toFixed(1)}h · Spécifique {c.heures_specifique.toFixed(1)}h · Projet {c.heures_projet.toFixed(1)}h
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default DashboardManagerV2;
